import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { CognitoUserSession } from "amazon-cognito-identity-js";
import * as auth from "./auth";
import type { AuthProvider, IdClaims } from "./auth";

export interface AuthUser {
  sub: string;
  email: string;
  name: string | null;
  groups: string[];
  isAdmin: boolean;
  isVerifiedMember: boolean;
  authProvider: AuthProvider;
}

export interface AuthContextValue {
  user: AuthUser | null;
  idToken: string | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, phone: string) => Promise<void>;
  confirmSignUp: (email: string, code: string, clientMetadata?: Record<string, string>) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  signOut: () => void;
  /** Re-checks for a session amazon-cognito-identity-js already cached outside
   *  signIn() -- used after the Facebook Hosted UI callback bridges tokens
   *  in via CognitoUser.setSignInUserSession() (see lib/facebookAuth.ts). */
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthContextValue | null>(null);

function userFromSession(session: CognitoUserSession): AuthUser {
  const claims: IdClaims = auth.getIdClaims(session);
  const groups = claims["cognito:groups"] ?? [];
  return {
    sub: claims.sub,
    email: claims.email ?? "",
    name: claims.name ?? null,
    groups,
    isAdmin: groups.includes("admins"),
    isVerifiedMember: groups.includes("verified-members"),
    authProvider: auth.getAuthProvider(claims),
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<CognitoUserSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Re-check for an existing, valid (refreshed-if-needed) session on mount so
  // a page reload doesn't bounce a signed-in member back to the login page.
  useEffect(() => {
    auth
      .getCurrentSession()
      .then(setSession)
      .finally(() => setIsLoading(false));
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const s = await auth.signIn(email, password);
    setSession(s);
  }, []);

  const signUp = useCallback(
    (email: string, password: string, phone: string) => auth.signUp(email, password, phone),
    [],
  );

  const confirmSignUp = useCallback(
    (email: string, code: string, clientMetadata?: Record<string, string>) =>
      auth.confirmSignUp(email, code, clientMetadata),
    [],
  );

  const resendConfirmationCode = useCallback(
    (email: string) => auth.resendConfirmationCode(email),
    [],
  );

  const forgotPassword = useCallback((email: string) => auth.forgotPassword(email), []);

  const confirmForgotPassword = useCallback(
    (email: string, code: string, newPassword: string) => auth.confirmForgotPassword(email, code, newPassword),
    [],
  );

  const signOut = useCallback(() => {
    auth.signOut();
    setSession(null);
  }, []);

  const refresh = useCallback(async () => {
    const s = await auth.getCurrentSession();
    setSession(s);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session ? userFromSession(session) : null,
      idToken: session ? session.getIdToken().getJwtToken() : null,
      isLoading,
      signIn,
      signUp,
      confirmSignUp,
      resendConfirmationCode,
      forgotPassword,
      confirmForgotPassword,
      signOut,
      refresh,
    }),
    [
      session,
      isLoading,
      signIn,
      signUp,
      confirmSignUp,
      resendConfirmationCode,
      forgotPassword,
      confirmForgotPassword,
      signOut,
      refresh,
    ],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
